import type { LifecycleEventLineItem, LifecycleEventTreeRow } from "./types";

interface GroupedLineItems {
  /** Flat list of parent + child + single rows for the tree-data grid. */
  rows: LifecycleEventTreeRow[];
  /** True when at least one multi-serial group exists (enables tree data). */
  hasGroups: boolean;
}

export const MULTIPLE_SERIALS_LABEL = "Multiple";

function buildGroupRows(
  parent: LifecycleEventLineItem,
  members: readonly LifecycleEventLineItem[],
): LifecycleEventTreeRow[] {
  const groupId = parent.groupId as string;

  // Synthetic parent
  const parentRow: LifecycleEventTreeRow = {
    ...parent,
    serialNumber: MULTIPLE_SERIALS_LABEL,
    quantity: parent.groupQuantity,
    previousPurchaseCost: parent.groupPreviousPurchaseCost,
    gm: parent.groupGm,
    _rowId: groupId,
    hierarchy: [groupId],
    _isSerialGroup: true,
  };

  const childRows = members.map((m, childIndex): LifecycleEventTreeRow => {
    const childId = `${groupId}-sn-${childIndex}`;
    return {
      serialNumber: m.serialNumber,
      quantity: m.quantity,
      previousPurchaseCost: m.previousPurchaseCost,
      gm: m.gm,
      _rowId: childId,
      hierarchy: [groupId, childId],
    };
  });

  return [parentRow, ...childRows];
}

export function groupLineItemsForDisplay(
  lineItems: readonly LifecycleEventLineItem[],
): GroupedLineItems {
  const groupsById = new Map<string, LifecycleEventLineItem[]>();
  // Bucket
  for (const item of lineItems) {
    if (!item.groupId) continue;
    const bucket = groupsById.get(item.groupId);
    if (bucket) bucket.push(item);
    else groupsById.set(item.groupId, [item]);
  }

  // Construct rows
  const rows = lineItems.flatMap((item, index): LifecycleEventTreeRow[] => {
    const members = item.groupId ? groupsById.get(item.groupId) : undefined;

    // Single row case
    if (!members || members.length < 2) {
      const id = `${item.soNumber ?? ""}-${item.serialNumber ?? ""}-${index}`;
      return [{ ...item, _rowId: id, hierarchy: [id] }];
    }

    // Skip if members already processed
    if (members[0] !== item) return [];

    return buildGroupRows(item, members);
  });

  return { rows, hasGroups: rows.some((row) => row._isSerialGroup) };
}
